import { formatMoneyFromCurrency } from '@utilities/theme';
import { EVENTS, subscribe } from '@utilities/events';
import { isVariantFullyAvailable } from '@utilities/product';

if(!customElements.get('product-bundle-overview')) {
  class ProductBundleOverview extends HTMLElement {
    constructor() {
      super();
      this.unsubscribers = [];
      this.boundHandleBundleItemVariantChange = this.handleBundleItemVariantChange.bind(this);
      this.boundHandlePromotionChange = this.handlePromotionChange.bind(this);
      this.boundSubmitBundleATC = this.submitBundleATC.bind(this);
    }

    connectedCallback() {
      this.init();
    }

    allowDebug() {
      return this.classList.contains('debug');
    }

    init(){
      // console.log(`ProductBundleOverview init()`, this);
      this.bundleId = this.generateBundleId();

      this.bundleTitle = this.dataset.title;

      this.combinedPriceAppend = "FOR COMPLETE LOOK";

      this.itemElements = this.querySelectorAll('product-bundle-item');
      // console.log(this.itemElements);

      this.promotionElements = this.querySelectorAll('product-promotion');
      // console.log(this.promotionElements);

      this.priceElements = this.querySelectorAll('.product__price-text');
      // console.log(this.priceElements);

      this.promotionPricingMessaging = this.querySelectorAll('.product__promotion-pricing-messaging');
      // console.log(this.promotionPricingMessaging);

      this.submitButton = this.querySelector('button[data-add-all-to-cart]');

      this.cartMessage = this.querySelector('.product__bundle__cart-message');

      this.promotion = {};

      this.initEventListeners();

      this.bundleItemVariants = [];

    }

    initEventListeners(){
      this.addEventListener('product-bundle-item:variant-change', this.boundHandleBundleItemVariantChange);
      this.unsubscribers.push(
        subscribe(EVENTS.PRODUCT_PROMOTION_CHANGE, this.boundHandlePromotionChange)
      );
      this.submitButton?.addEventListener("click", this.boundSubmitBundleATC);
    }

    generateBundleId(){
      this.now = new Date();
      return `bundle-${this.id}-${this.now.getTime()}`;
    }

    async handleBundleItemVariantChange(event) {
      // Check if the event's triggering element is a descendant of this component
      if (this.contains(event.target)) {
        // Handle the event, knowing it originated from a descendant <product-bundle-item>
        this.allowDebug() && console.log('ProductBundleOverview variant change detected in a child product-bundle-item', event.target);
        this.allowDebug() && console.log('ProductBundleOverview variant', event.detail.variant);
        this.clearCartMessage();
        this.setBundleVariants();
        await this.updatePriceElements();
        this.updateBundleATC();
        this.dispatchEvent(new CustomEvent('product-bundle-overview:variant-change', {
          bubbles: true,
          detail: { variants: this.getBundleVariants() }
        }));
      }
    }

    setBundleVariants(){
      if(!this.itemElements) return;
      this.bundleItemVariants = [];
      this.itemElements.forEach((itemElement)=>{
        this.bundleItemVariants.push(itemElement.getVariant());
      });
    }

    getBundleVariants(){
      return this.bundleItemVariants;
    }

    async handlePromotionChange(data){
      // Check if the event's triggering element is a descendant of this component
      if (this.contains(data.triggeringElement)) {
        // Handle the event, knowing it originated from a descendant <product-promotion>
        // console.log('ProductBundleOverview promotion change detected in a child product-bundle-item', data.triggeringElement);
        this.promotion = data.promotion;
        // console.log('ProductBundleOverview promotion', this.promotion);
        await this.updatePriceElements();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('product-bundle-item:variant-change', this.boundHandleBundleItemVariantChange);
      this.submitButton?.removeEventListener("click", this.boundSubmitBundleATC);
      if (this.unsubscribers?.length) {
        this.unsubscribers.forEach((unsub) => unsub());
        this.unsubscribers = [];
      }
    }

    async updatePriceElements(){
      // console.log(`ProductBundleOverview updatePriceElements()`);
      if(this.priceElements.length === 0) return;

      /*
        If this.promotion has keys, we have a promotion.
        We want to see if the promotion returned has the property applies_discount && get the property discount type.
        If the discount type is "percent_off" we want to store that value.
        TODO: cover amount_off
      */

      let percentOff = 0;
      if (Object.keys(this.promotion).length > 0) {
        if(this.promotion.fields.applies_discount && this.promotion.fields.hasOwnProperty("percent_off")){
          percentOff = this.promotion.fields.percent_off;
        }
      }

      /*
        Next, we want to loop through this.itemElements, and call their "getVariant()" function. This will return an object with important information.
        We want to add up the "price" property of each one.
        If we have percent off data, we want another variable that is the total itemElements cost with the discount applied.
      */

      let bundleTotal = 0;
      let discountedBundleTotal = 0;
      this.itemElements.forEach((item) => {
        const variant = item.getVariant();
        if (variant && variant.price) {
          bundleTotal += variant.price;
        }
      });

      // console.log(`bundleTotal: ${bundleTotal}`);

      if (percentOff > 0) {
        discountedBundleTotal = (bundleTotal - (bundleTotal * (percentOff / 100)));
        // console.log(`discountedBundleTotal: ${discountedBundleTotal}`);
      }

      // activate pricing messaging
      if(this.promotion?.hasOwnProperty('disclaimer') && this.promotionPricingMessaging){
        this.promotionPricingMessaging.forEach((pricingMessage)=>{
          pricingMessage.innerHTML = this.promotion.fields.disclaimer;
          pricingMessage.classList.remove('hidden');
        });
      }

      const currencyCode = window.theme?.currency?.code || 'USD';
      const bundleTotalFormatted = formatMoneyFromCurrency(bundleTotal, currencyCode);
      const discountedBundleTotalFormatted = formatMoneyFromCurrency(discountedBundleTotal, currencyCode);

      // Update the price display
      let priceContent = ``;
      if(discountedBundleTotal > 0){
        priceContent = `<span class="strikethrough product__price-light">${bundleTotalFormatted}</span><span>${discountedBundleTotalFormatted }</span><span class="product__price-append">${this.combinedPriceAppend}</span>`;
      } else {
        priceContent = `<span>${bundleTotalFormatted}</span><span class="product__price-append">${this.combinedPriceAppend}</span>`;
      }

      this.priceElements.forEach(priceElement => {
        priceElement.innerHTML = `${priceContent}`;
      });
    }

    async updateBundleATC() {
      let allVariantsAvailable = true;

      if(!this.submitButton) return;

      const buttonText = this.submitButton.querySelector('.button__text');

      for (const itemElement of this.itemElements) {
        const currentVariant = itemElement.getVariant();
        if (!isVariantFullyAvailable(currentVariant)) {
          allVariantsAvailable = false;
          break; // Exit the loop if any variant is not available
        }
      }

      if (allVariantsAvailable) {
        // Enable the submit button if all variants are available
        this.submitButton.classList.remove('disabled');
        this.submitButton.removeAttribute('disabled');
        this.submitButton.removeAttribute('aria-disabled');
        // Reset the button message to indicate the user can add the bundle to the bag
        if (buttonText) {
          buttonText.innerHTML = this.submitButton.dataset.availableText;
        }
        // Reset the cart message to indicate the user should make sure they have the right options selected
        if (this.cartMessage) {
          this.cartMessage.innerHTML = "Please make sure you've selected the correct sizes.";
        }
      } else {
        // Disable the submit button if any variant is not available
        this.submitButton.classList.add('disabled');
        this.submitButton.setAttribute('disabled', 'disabled');
        this.submitButton.setAttribute('aria-disabled', 'true');
        // Update the button message to indicate that not all items are available
        if (buttonText) {
          buttonText.innerHTML = this.submitButton.dataset.unavailableText;
        }
        // Update the cart message to indicate that not all items are available
        if (this.cartMessage) {
          this.cartMessage.innerHTML = "Please select a different combination.";
        }
      }
    }

    async submitBundleATC(event){
      event.preventDefault();
      if(!this.submitButton) return;

      try{
        // Clear any existing cart messages
        this.clearCartMessage();

        // Set loading state
        this.setLoadingState(true);

        // Prepare bundle items
        const bundleItems = this.prepareBundleItems();

        if(bundleItems.length === 0) {
          this.bundleId = this.generateBundleId();
          this.setLoadingState(false);
          return;
        }

        if(bundleItems.length !== this.itemElements.length){
          // All bundle components must be available (all or nothing)
          this.bundleId = this.generateBundleId();
          this.setLoadingState(false);
          return;
        }

        // Use standardized cart API preserving DOM item order.
        const { addToCart } = await import('@utilities/theme');
        await addToCart(bundleItems);

        // Show success message
        if(this.cartMessage){
          this.cartMessage.innerHTML = `Please make sure you've selected the correct sizes.`;
          this.cartMessage.classList.remove('error');
        }

        // Close quickview modal after successful add to cart
        const quickview = this.closest('quickview-modal');
        if(quickview) quickview.close();

        // Generate new bundle ID for next submission
        this.bundleId = this.generateBundleId();

        // Reset loading state
        this.setLoadingState(false);

      } catch(error){
        console.error('Bundle Overview: Error adding bundle to cart:', error);

        // Show error message
        this.showCartError(error.message || 'Error adding bundle to cart');

        // Reset loading state
        this.setLoadingState(false);
      }
    }

    /**
     * Prepare bundle items for cart submission
     * @returns {Array} Array of cart items
     */
    prepareBundleItems() {
      const send_items = [];

      if(this.itemElements.length > 0){
        this.itemElements.forEach((bundleProduct) => {
          const bundleVariant = bundleProduct.getVariant();
          if (!isVariantFullyAvailable(bundleVariant)) return;

          const properties = this.collectBundleItemProperties(bundleProduct, bundleVariant);
          send_items.push({
            id: bundleVariant.id,
            quantity: 1,
            properties
          });
        });
      }

      return send_items;
    }

    /**
     * Resolve host bundle parent variant ID from the add-all form.
     * @returns {number|null} Host variant ID or null
     */
    getHostBundleParentVariantId() {
      const idInput = this.querySelector('.product-bundle-overview__form input[name="id"]');
      const variantId = parseInt(idInput?.value, 10);
      return Number.isFinite(variantId) ? variantId : null;
    }

    /**
     * Calculate total price for currently selected bundle variants.
     * @returns {number} Bundle total in cents
     */
    calculateBundleTotal() {
      let bundleTotal = 0;
      this.itemElements.forEach((item) => {
        const variant = item.getVariant();
        if (variant?.price) {
          bundleTotal += variant.price;
        }
      });
      return bundleTotal;
    }

    /**
     * Collect properties for a bundle item
     * @param {Element} bundleProduct - Bundle product element
     * @param {Object} bundleVariant - Bundle variant object
     * @returns {Object} Properties object
     */
    collectBundleItemProperties(bundleProduct, bundleVariant) {
      let properties = {};

      // Bundle merge properties (CYO-compatible contract)
      const hostVariantId = this.getHostBundleParentVariantId();
      const bundledData = {
        _bundled_id: this.bundleId,
        _bundle_parent: hostVariantId
          ? `gid://shopify/ProductVariant/${hostVariantId}`
          : null,
        _bundle_title: this.bundleTitle,
        _bundle_cost: this.calculateBundleTotal(),
        _bundled_component_price: bundleVariant?.price || 0
      };
      properties["_bundled_id"] = this.bundleId;
      properties["_bundled_type"] = "merge";
      if (bundledData._bundle_parent) {
        properties["_bundle_parent"] = bundledData._bundle_parent;
      }
      properties["_bundled_data"] = JSON.stringify(bundledData);
      properties["_ord_type"] = bundleProduct.getOrderType();

      if(bundleVariant.hasOwnProperty("final_sale") && bundleVariant.final_sale === true){
        properties["_Final Sale"] = true;
      }

      if(bundleVariant.hasOwnProperty("color")){
        properties["_color_id"] = bundleVariant.color;
      }

      // Collect promotion properties
      if (Object.keys(this.promotion).length > 0) {
        const promotionProperties = window.theme?.utilities?.getPromotionProperties(this.promotion);
        if (Object.keys(promotionProperties).length > 0) {
          Object.entries(promotionProperties).forEach(([key, value]) => {
            properties[`${key}`] = value.value;
          });
        }
      }

      // Collect hidden form properties from bundle product
      if(bundleProduct.form){
        bundleProduct.form.querySelectorAll(`input[type="hidden"][name^="properties["]`).forEach(el => {
          const propertyName = el.name.match(/\[([^)]+)\]/)?.[1];
          if (propertyName) properties[propertyName] = el.value;
        });
      }

      // Collect hidden form properties from bundle overview
      this.querySelectorAll(`.product__bundle__add-all-container input[type="hidden"][name^="properties["]`).forEach(el => {
        const propertyName = el.name.match(/\[([^)]+)\]/)?.[1];
        if (propertyName) properties[propertyName] = el.value;
      });

      properties["_add_to_cart_component"] = "product-bundle-overview";

      return properties;
    }

    /**
     * Set loading state for submit button
     * @param {boolean} isLoading - Whether to show loading state
     */
    setLoadingState(isLoading) {
      if (!this.submitButton) return;

      if (isLoading) {
        this.submitButton.classList.add('disabled');
        this.submitButton.setAttribute('disabled', 'disabled');
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.submitButton.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
      } else {
        this.submitButton.classList.remove('loading');
        this.submitButton.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        this.updateBundleATC();
      }
    }

    /**
     * Clear cart error message
     */
    clearCartMessage() {
      if (this.cartMessage) {
        this.cartMessage.innerHTML = "";
        this.cartMessage.classList.remove('error');
      }
    }

    /**
     * Show cart error message
     * @param {string} message - Error message to display
     */
    showCartError(message) {
      if (this.cartMessage) {
        this.cartMessage.innerHTML = message;
        this.cartMessage.classList.add('error');
      }
    }
  }

  customElements.define('product-bundle-overview', ProductBundleOverview);
}
